import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload as UploadIcon, Loader2, CheckCircle, AlertTriangle, Film } from 'lucide-react';
import { Upload as TusUpload } from 'tus-js-client';
import { toast } from 'sonner';
import { supabase } from '@/lib/customSupabaseClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const FUNCTION_NAME = 'vimeo-video-settings';
const POLL_INTERVAL_MS = 10000;
const PROCESSING_LABELS = {
  uploading: 'Waiting for Vimeo to receive the file…',
  transcode_starting: 'Vimeo is starting to process the video…',
  transcoding: 'Vimeo is processing the video…',
};
const FAILED_LABELS = {
  uploading_error: 'Vimeo reported an upload error.',
  transcoding_error: 'Vimeo could not process this file.',
  quota_exceeded: 'Your Vimeo upload quota is used up.',
  total_cap_exceeded: 'Your Vimeo storage is full.',
};

const storageKey = (exerciseId) => `vimeo-upload:${exerciseId}`;

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

async function invokeSettings(body) {
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, { body });
  if (error) {
    let detail = null;
    try {
      detail = await error.context?.json?.();
    } catch {
      detail = null;
    }
    throw new Error(detail?.error || error.message);
  }
  if (!data?.success) throw new Error(data?.error || 'Vimeo request failed');
  return data;
}

export function VideoUploadPanel({ exercise, folders, onLinked }) {
  const exerciseId = exercise.id;
  const hasVideo = Boolean(exercise.vimeo_video_id || exercise.vimeo_link);
  const [file, setFile] = useState(null);
  const [folder, setFolder] = useState(() => (/kettlebell/i.test(exercise.name || '') ? 'kettlebell' : ''));
  const [phase, setPhase] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [processingState, setProcessingState] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const uploadRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);
  const phaseRef = useRef(phase);
  const onLinkedRef = useRef(onLinked);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    onLinkedRef.current = onLinked;
  }, [onLinked]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fail = useCallback((message) => {
    stopPolling();
    localStorage.removeItem(storageKey(exerciseId));
    setErrorMessage(message);
    setPhase('failed');
  }, [exerciseId, stopPolling]);

  const poll = useCallback(async (vimeoId) => {
    try {
      const data = await invokeSettings({ exerciseId, action: 'video_status', vimeoId });
      if (data.state === 'linked') {
        stopPolling();
        localStorage.removeItem(storageKey(exerciseId));
        setPhase('linked');
        toast.success('Video uploaded and linked', { description: 'Set the thumbnail next if needed.' });
        await onLinkedRef.current?.();
        return;
      }
      if (data.failed) {
        fail(FAILED_LABELS[data.state] || `Vimeo reported: ${data.state}`);
        return;
      }
      setProcessingState(data.state);
    } catch (err) {
      setProcessingState(`Could not check status (${err.message}); retrying…`);
    }
    pollRef.current = setTimeout(() => poll(vimeoId), POLL_INTERVAL_MS);
  }, [exerciseId, fail, stopPolling]);

  const startProcessing = useCallback((vimeoId) => {
    setPhase('processing');
    setProcessingState('uploading');
    stopPolling();
    poll(vimeoId);
  }, [poll, stopPolling]);

  useEffect(() => {
    const pendingVimeoId = localStorage.getItem(storageKey(exerciseId));
    if (pendingVimeoId) startProcessing(pendingVimeoId);
    return () => {
      stopPolling();
      if (uploadRef.current) {
        uploadRef.current.abort();
        if (phaseRef.current === 'uploading') localStorage.removeItem(storageKey(exerciseId));
      }
    };
  }, [exerciseId, startProcessing, stopPolling]);

  useEffect(() => {
    if (phase !== 'uploading') return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  const chooseFile = (event) => {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;
    if (!chosen.type.startsWith('video/')) {
      toast.error('Choose a video file');
      return;
    }
    setFile(chosen);
    setErrorMessage(null);
    setPhase('idle');
  };

  const startUpload = async () => {
    if (!file || !folder) return;
    setErrorMessage(null);
    setPhase('creating');
    setProgress(0);

    try {
      const { quota } = await invokeSettings({ exerciseId, action: 'quota' });
      if (quota?.free != null && file.size > quota.free) {
        throw new Error(`Not enough Vimeo space: ${formatBytes(quota.free)} free, file is ${formatBytes(file.size)}.`);
      }

      const created = await invokeSettings({ exerciseId, action: 'video_create', folder, size: file.size });
      if (!created.folderAdded) {
        toast.warning('Uploaded outside the folder', { description: 'Vimeo did not accept the folder; move the video on Vimeo later.' });
      }
      localStorage.setItem(storageKey(exerciseId), created.vimeoId);
      setPhase('uploading');

      const upload = new TusUpload(file, {
        uploadUrl: created.uploadLink,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        metadata: { filename: file.name, filetype: file.type },
        onProgress: (sent, total) => setProgress(Math.round((sent / total) * 100)),
        onError: (err) => fail(`Upload failed: ${err.message}`),
        onSuccess: () => {
          uploadRef.current = null;
          setFile(null);
          startProcessing(created.vimeoId);
        },
      });
      uploadRef.current = upload;
      upload.start();
    } catch (err) {
      fail(err.message);
    }
  };

  const cancelUpload = () => {
    uploadRef.current?.abort();
    uploadRef.current = null;
    localStorage.removeItem(storageKey(exerciseId));
    setPhase('idle');
    setProgress(0);
    toast.info('Upload cancelled', { description: 'An empty video entry may remain on Vimeo.' });
  };

  const stopWaiting = () => {
    stopPolling();
    localStorage.removeItem(storageKey(exerciseId));
    setPhase('idle');
    toast.info('Stopped checking', { description: 'If Vimeo finishes later, link it with Sync Vimeo.' });
  };

  const busy = ['creating', 'uploading', 'processing'].includes(phase);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-base">
          <Film className="h-4 w-4 mr-2 text-muted-foreground" />
          {hasVideo ? 'Replace Video' : 'Upload Video'}
        </CardTitle>
        <CardDescription>
          Uploads to Vimeo as "{exercise.name}" (Unlisted, downloads off, embedding everywhere) and links it here.
          {hasVideo && ' The current video stays on Vimeo but is unlinked from this exercise only.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {phase === 'processing' ? (
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span>{PROCESSING_LABELS[processingState] || processingState || 'Processing on Vimeo…'}</span>
            <span className="text-xs text-muted-foreground">You can leave this page; it continues when you return.</span>
            <Button variant="ghost" size="sm" onClick={stopWaiting}>Stop waiting</Button>
          </div>
        ) : phase === 'linked' ? (
          <div className="flex items-center gap-2 text-sm text-emerald-600">
            <CheckCircle className="h-4 w-4" /> Video linked.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={folder} onValueChange={setFolder} disabled={busy}>
                <SelectTrigger className="h-8 w-[180px] text-xs">
                  <SelectValue placeholder="Vimeo folder..." />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {folders.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-xs">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                <UploadIcon className="h-3.5 w-3.5 mr-1.5" />
                {file ? 'Change file' : 'Choose video'}
              </Button>
              <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={chooseFile} />
              {file && (
                <span className="text-xs text-muted-foreground">
                  {file.name} · {formatBytes(file.size)}
                </span>
              )}
            </div>

            {phase === 'uploading' || phase === 'creating' ? (
              <div className="space-y-2">
                <Progress value={progress} />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{phase === 'creating' ? 'Preparing upload…' : `Uploading to Vimeo… ${progress}%`}</span>
                  {phase === 'uploading' && (
                    <Button variant="ghost" size="sm" onClick={cancelUpload}>Cancel</Button>
                  )}
                </div>
              </div>
            ) : (
              <Button size="sm" onClick={startUpload} disabled={!file || !folder}>
                <UploadIcon className="h-3.5 w-3.5 mr-1.5" />
                {hasVideo ? 'Upload and replace' : 'Upload'}
              </Button>
            )}
          </>
        )}

        {errorMessage && (
          <div className="flex items-start gap-2 rounded-md border border-red-500/20 bg-red-500/10 p-3 text-xs text-red-500">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
